import React, { useState, useEffect } from 'react';
import { Construction, MessageSquare, Image as ImageIcon, Layout, BarChart3, Send, Loader2, AlertCircle, ExternalLink, Plus, Trash2, Pin, MessageCircle, X, ArrowRight, CheckCircle } from 'lucide-react';
import { moduleService } from '../../../services/modules';
import { MiniAppShell } from './MiniAppShell';

interface WebsiteBuilderAppProps {
    onExecutionStarted: (executionId: string) => void;
    onBack: () => void;
}

type JourneyStage = 'INTAKE' | 'INSPIRATION' | 'GENERATION' | 'MANAGEMENT';

export const WebsiteBuilderApp = ({ onExecutionStarted, onBack }: WebsiteBuilderAppProps) => {
    const [stage, setStage] = useState<JourneyStage>('INTAKE');
    const [projectData, setProjectData] = useState<{
        projectId?: string;
        questionnaire?: any;
        selection?: any;
    }>({});

    const navItems = [
        { id: 'sandbox', label: 'AI Sandbox', icon: Layout },
        { id: 'assets', label: 'Asset Library', icon: ImageIcon },
        { id: 'cms', label: 'Content CMS', icon: MessageSquare },
        { id: 'stats', label: 'Site Intelligence', icon: BarChart3 },
    ];

    return (
        <MiniAppShell
            title="Website Builder"
            icon={Construction}
            navItems={navItems}
            onBack={onBack}
        >
            {(activeTab, setActiveTab) => {
                if (activeTab === 'sandbox') {
                    return (
                        <div className="h-full">
                            {stage === 'INTAKE' && (
                                <IntakeWizard
                                    onComplete={(data, result) => {
                                        setProjectData(prev => ({ ...prev, questionnaire: data, projectId: result.projectId }));
                                        setStage('INSPIRATION');
                                    }}
                                />
                            )}
                            {stage === 'INSPIRATION' && (
                                <InspirationGallery
                                    projectId={projectData.projectId!}
                                    questionnaire={projectData.questionnaire}
                                    onComplete={(selection) => {
                                        setProjectData(prev => ({ ...prev, selection }));
                                        setStage('GENERATION');
                                    }}
                                />
                            )}
                            {stage === 'GENERATION' && (
                                <GenerationSandbox
                                    projectId={projectData.projectId!}
                                    selection={projectData.selection}
                                    questionnaire={projectData.questionnaire}
                                    onExecutionStarted={onExecutionStarted}
                                />
                            )}
                        </div>
                    );
                }

                switch (activeTab) {
                    case 'assets':
                        return <AssetsView projectId={projectData.projectId!} />;
                    case 'cms':
                        return <CMSView projectId={projectData.projectId!} />;
                    case 'stats':
                        return <StatsView />;
                    default:
                        return <div className="p-8 text-center text-slate-400">Select a tab to begin</div>;
                }
            }}
        </MiniAppShell>
    );
};

const GenerationSandbox = ({ projectId, selection, questionnaire, onExecutionStarted }: { projectId: string; selection: any; questionnaire: any; onExecutionStarted: (id: string) => void }) => {
    const [messages, setMessages] = useState([
        { role: 'ai', content: 'Project initialized! I have your brand DNA and design inspirations. Click "Generate Initial Site" to start the AI pipeline.' }
    ]);
    const [prompt, setPrompt] = useState('');
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [currentStatus, setCurrentStatus] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Visual Canvas State
    const [comments, setComments] = useState<any[]>([]);
    const [isAddingComment, setIsAddingComment] = useState(false);
    const [pendingCommentPos, setPendingCommentPos] = useState({ x: 0, y: 0 });
    const [commentText, setCommentText] = useState('');

    const fetchComments = async () => {
        try {
            const result = await moduleService.executeModule('website_builder', { action: 'listComments', projectId });
            setComments(result.comments);
        } catch (err: any) {
            console.error('Failed to fetch comments:', err);
        }
    };

    useEffect(() => {
        if (previewUrl) {fetchComments();}
    }, [previewUrl]);

    const handleAddComment = async () => {
        if (!commentText) {return;}
        try {
            const result = await moduleService.executeModule('website_builder', {
                action: 'addComment',
                projectId,
                comment: {
                    x: pendingCommentPos.x,
                    y: pendingCommentPos.y,
                    content: commentText,
                    author: 'Client' // Hardcoded for now
                }
            });
            setComments(prev => [...prev, result.comment]);
            setCommentText('');
            setIsAddingComment(false);
        } catch (err: any) {
            alert('Failed to save comment');
        }
    };

    const businessData = {
        name: questionnaire.business_name,
        industry: questionnaire.industry,
        productsServices: questionnaire.products_services,
        targetAudience: questionnaire.target_audience,
        goals: questionnaire.business_goals,
    };

    const handleGenerate = async (isRevision = false) => {
        setLoading(true);
        setError(null);
        try {
            const result = isRevision
                ? await moduleService.executeModule('website_builder', { action: 'handleReviewFeedback', projectId, feedback: prompt })
                : await moduleService.executeModule('website_builder', { action: 'generateWebsite', designSelection: selection, businessData });

            onExecutionStarted(result.projectId || result.executionId);

            // Polling for Status and Preview URL
            const pollInterval = setInterval(async () => {
                try {
                    const statusRes = await moduleService.executeModule('website_builder', { action: 'getProjectStatus', projectId });
                    setCurrentStatus(statusRes.status);

                    if (statusRes.previewUrl && statusRes.status === 'Client_Review_Phase') {
                        setPreviewUrl(statusRes.previewUrl);
                        setMessages(prev => [...prev, { role: 'ai', content: 'Site generated and approved! You can now see the live preview. How does it look?' }]);
                        clearInterval(pollInterval);
                    } else if (statusRes.status === 'HITL_Review_Pending') {
                        setMessages(prev => [...prev, { role: 'ai', content: 'Your site has been generated and is now awaiting internal admin review. We will notify you once it is approved for your viewing!' }]);
                        clearInterval(pollInterval);
                    }
                } catch (e) {
                    console.error('Polling error:', e);
                }
            }, 3000);

            if (!isRevision) {
                setMessages(prev => [...prev, { role: 'ai', content: 'Starting the generation process... I am now synthesizing your brand DNA into a live site. This usually takes 1-2 minutes.' }]);
            } else {
                const hasComments = comments.length > 0;
                setMessages(prev => [...prev, {
                    role: 'ai',
                    content: `Applying your feedback: "${prompt}"${hasComments ? ` and analyzing your ${comments.length} visual pin comments` : ''}. Regenerating the site...`
                }]);
            }
        } catch (err: any) {
            setError(err.message || 'Generation failed');
        } finally {
            setLoading(false);
        }
    };

    const handleSendMessage = async () => {
        if (!prompt) {return;}
        const userMsg = prompt;
        setPrompt('');
        setMessages(prev => [...prev, { role: 'user', content: userMsg }]);

        if (!previewUrl) {
            setMessages(prev => [...prev, { role: 'ai', content: 'Please generate the initial site first before providing iterative feedback.' }]);
            return;
        }

        await handleGenerate(true);
    };

    const handleDeploy = async () => {
        setLoading(true);
        setError(null);
        try {
            const result = await moduleService.executeModule('website_builder', { action: 'triggerDeployment', projectId });
            setMessages(prev => [...prev, { role: 'ai', content: `Website deployed successfully! Your live site is now available at ${result.repoUrl}.` }]);
            alert(`Success! Your site is live at ${result.repoUrl}`);
        } catch (err: any) {
            setError(err.message || 'Deployment failed');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="flex h-full gap-6 overflow-hidden relative">
            {/* State Overlay */}
            {currentStatus === 'AI_Processing_Active' && (
                <div className="absolute inset-0 z-50 bg-white/80 backdrop-blur-sm flex flex-col items-center justify-center text-center p-8 transition-all">
                    <div className="p-8 bg-white rounded-full shadow-2xl mb-6 animate-bounce">
                        <Loader2 size={48} className="animate-spin text-blue-600" />
                    </div>
                    <h3 className="text-xl font-black text-slate-800 uppercase tracking-tight mb-2">AI is Synthesizing Your Brand DNA</h3>
                    <p className="text-slate-500 font-medium max-w-md mx-auto">
                        Our AI agents are currently crafting your custom layout, selecting optimal typography, and generating a high-conversion structure based on your inputs.
                    </p>
                    <div className="mt-8 flex gap-2">
                        <div className="w-2 h-2 rounded-full bg-blue-600 animate-pulse" />
                        <div className="w-2 h-2 rounded-full bg-blue-600 animate-pulse delay-75" />
                        <div className="w-2 h-2 rounded-full bg-blue-600 animate-pulse delay-150" />
                    </div>
                </div>
            )}

            {/* AI Chat Panel */}
            <div className="w-1/3 flex flex-col bg-white border border-slate-200 rounded-3xl shadow-sm overflow-hidden">
                <div className="p-6 border-b border-slate-100 bg-slate-50">
                    <h3 className="text-sm font-black text-slate-800 uppercase tracking-tight">AI Design Assistant</h3>
                    <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">Iterative Refinement</p>
                </div>

                <div className="flex-1 p-6 overflow-y-auto space-y-4">
                    {messages.map((m, i) => (
                        <div key={i} className={`p-4 rounded-2xl text-xs leading-relaxed ${m.role === 'ai' ? 'bg-slate-100 text-slate-600 rounded-tl-none' : 'bg-blue-600 text-white rounded-tr-none ml-8'}`}>
                            {m.content}
                        </div>
                    ))}
                    {loading && (
                        <div className="p-4 bg-slate-100 rounded-2xl rounded-tl-none text-slate-400 text-xs flex items-center gap-2 italic">
                            <Loader2 size={12} className="animate-spin" /> AI is thinking...
                        </div>
                    )}
                </div>

                <div className="p-6 border-t border-slate-100 space-y-4">
                    {!previewUrl ? (
                        <button
                            onClick={() => handleGenerate()}
                            disabled={loading}
                            className="w-full py-3 bg-blue-600 text-white rounded-xl text-xs font-black uppercase tracking-widest hover:bg-blue-700 transition-all disabled:opacity-50"
                        >
                            {loading ? <Loader2 size={18} className="animate-spin" /> : 'Generate Initial Site'}
                        </button>
                    ) : (
                        <button
                            onClick={handleDeploy}
                            disabled={loading}
                            className="w-full py-3 bg-emerald-600 text-white rounded-xl text-xs font-black uppercase tracking-widest hover:bg-emerald-700 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                        >
                            {loading ? <Loader2 size={18} className="animate-spin" /> : '🚀 Deploy to Production'}
                        </button>
                    )}
                    <div className="relative">
                        <textarea
                            value={prompt}
                            onChange={(e) => setPrompt(e.target.value)}
                            placeholder={previewUrl ? "e.g. Make the hero section more bold..." : "Initialize project first..."}
                            disabled={!previewUrl}
                            className="w-full p-4 pr-12 rounded-2xl border border-slate-200 text-sm focus:ring-2 ring-purple-500 outline-none transition-all resize-none"
                            rows={3}
                        />
                        <button
                            onClick={handleSendMessage}
                            disabled={loading || !prompt || !previewUrl}
                            className="absolute bottom-3 right-3 p-2 bg-slate-900 text-white rounded-xl hover:bg-slate-800 transition-all disabled:opacity-50"
                        >
                            {loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                        </button>
                    </div>
                    {error && (
                        <div className="p-3 bg-red-50 border border-red-100 text-red-600 text-[10px] font-bold flex items-center gap-2 rounded-lg">
                            <AlertCircle size={12} /> {error}
                        </div>
                    )}
                </div>
            </div>

            {/* Preview Panel */}
            <div className="flex-1 bg-white border border-slate-200 rounded-3xl shadow-sm overflow-hidden flex flex-col">
                <div className="h-12 border-b border-slate-100 px-4 flex items-center justify-between bg-slate-50">
                    <div className="flex items-center gap-2">
                        <div className="flex gap-1.5 px-3 py-1 rounded-full bg-white border border-slate-200">
                            <div className="w-2 h-2 rounded-full bg-red-400" />
                            <div className="w-2 h-2 rounded-full bg-amber-400" />
                            <div className="w-2 h-2 rounded-full bg-emerald-400" />
                        </div>
                        <div className="text-[10px] font-mono text-slate-400 ml-4 truncate max-w-xs">
                            {previewUrl || 'waiting-for-generation.local'}
                        </div>
                    </div>
                    {previewUrl && (
                        <a
                            href={previewUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="text-[10px] font-bold text-blue-600 uppercase tracking-widest flex items-center gap-1 hover:underline"
                        >
                            Open Full Page <ExternalLink size={12} />
                        </a>
                    )}
                </div>
                <div className="flex-1 bg-slate-200 overflow-hidden relative group">
                    {previewUrl ? (
                        <>
                            <iframe src={previewUrl} className="w-full h-full border-none" title="Site Preview" />

                            {/* Visual Canvas Interaction Layer */}
                            <div
                                className="absolute inset-0 cursor-crosshair"
                                onClick={(e) => {
                                    const rect = e.currentTarget.getBoundingClientRect();
                                    setPendingCommentPos({
                                        x: ((e.clientX - rect.left) / rect.width) * 100,
                                        y: ((e.clientY - rect.top) / rect.height) * 100,
                                    });
                                    setIsAddingComment(true);
                                }}
                            >
                                {comments.map((c, i) => (
                                    <div
                                        key={i}
                                        className="absolute w-6 h-6 -ml-3 -mt-3 group/pin cursor-pointer"
                                        style={{ left: `${c.x}%`, top: `${c.y}%` }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            alert(`Comment: ${c.content}`);
                                        }}
                                    >
                                        <div className="p-1.5 bg-blue-600 text-white rounded-full shadow-lg hover:scale-110 transition-transform">
                                            <Pin size={12} />
                                        </div>
                                        <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 px-2 py-1 bg-white text-slate-800 text-[10px] font-bold rounded-lg shadow-md opacity-0 group-hover/pin:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                                            {c.content}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Add Comment Modal */}
                            {isAddingComment && (
                                <div className="absolute z-50 bg-white p-3 rounded-2xl shadow-xl border border-slate-200 flex flex-col gap-2 min-w-[200px]"
                                     style={{
                                         left: `${pendingCommentPos.x}%`,
                                         top: `${pendingCommentPos.y}%`,
                                         transform: 'translate(-50%, -50%)'
                                     }}>
                                    <div className="flex items-center justify-between mb-1">
                                        <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">New Comment</span>
                                        <button onClick={() => setIsAddingComment(false)} className="p-1 hover:bg-slate-100 rounded-lg text-slate-400"><X size={12} /></button>
                                    </div>
                                    <textarea
                                        autoFocus
                                        value={commentText}
                                        onChange={e => setCommentText(e.target.value)}
                                        placeholder="What needs changing here?"
                                        className="w-full p-2 text-xs border border-slate-200 rounded-lg outline-none focus:ring-2 ring-blue-500 resize-none"
                                        rows={3}
                                    />
                                    <button
                                        onClick={handleAddComment}
                                        className="w-full py-2 bg-blue-600 text-white rounded-lg text-[10px] font-bold uppercase tracking-widest hover:bg-blue-700"
                                    >
                                        Save Pin
                                    </button>
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-8">
                            <div className="p-6 bg-white rounded-full text-slate-300 mb-4">
                                <Layout size={48} />
                            </div>
                            <h3 className="text-slate-800 font-black uppercase tracking-tight text-sm">No Preview Available</h3>
                            <p className="text-slate-400 text-xs font-medium max-w-xs mt-2">
                                Start the generation process to see your website come to life here.
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const IntakeWizard = ({ onComplete }: { onComplete: (data: any, result: any) => void }) => {
    const [formData, setFormData] = useState({
        business_name: '',
        industry: '',
        products_services: '',
        target_audience: '',
        business_goals: '',
        color_preferences: '',
        content_focus: '',
        style: 'modern',
        layout: 'standard',
        inspiration_keywords: '',
        additional_requirements: '',
        brand_voice: '',
        competitors: '',
        primary_cta: '',
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleSubmit = async () => {
        if (!formData.business_name || !formData.industry) {
            setError('Business Name and Industry are required');
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const processedData = {
                ...formData,
                business_goals: formData.business_goals.split(',').map(s => s.trim()).filter(Boolean),
                color_preferences: formData.color_preferences.split(',').map(s => s.trim()).filter(Boolean),
                content_focus: formData.content_focus.split(',').map(s => s.trim()).filter(Boolean),
                inspiration_keywords: formData.inspiration_keywords.split(',').map(s => s.trim()).filter(Boolean),
                competitors: formData.competitors.split(',').map(s => s.trim()).filter(Boolean),
            };
            const result = await moduleService.executeModule('website_builder', {
                action: 'processQuestionnaire',
                data: processedData,
            });
            onComplete(processedData, result);
        } catch (err: any) {
            setError(err.message || 'Intake failed');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="max-w-4xl mx-auto p-8 space-y-8">
            <div className="text-center space-y-2">
                <h2 className="text-3xl font-black text-slate-800 uppercase tracking-tight">Project Intake</h2>
                <p className="text-slate-400 font-medium">Let's define the DNA of your new website</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-white p-8 rounded-3xl border border-slate-200 shadow-sm">
                <div className="space-y-4">
                    <div>
                        <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest block mb-1">Business Name *</label>
                        <input type="text" value={formData.business_name} onChange={e => setFormData({ ...formData, business_name: e.target.value })} className="w-full px-4 py-2 rounded-xl border border-slate-200 text-sm focus:ring-2 ring-blue-500 outline-none" placeholder="e.g. Nova Luxury Watches" />
                    </div>
                    <div>
                        <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest block mb-1">Industry *</label>
                        <input type="text" value={formData.industry} onChange={e => setFormData({ ...formData, industry: e.target.value })} className="w-full px-4 py-2 rounded-xl border border-slate-200 text-sm focus:ring-2 ring-blue-500 outline-none" placeholder="e.g. High-End Horology" />
                    </div>
                    <div>
                        <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest block mb-1">Products & Services</label>
                        <textarea value={formData.products_services} onChange={e => setFormData({ ...formData, products_services: e.target.value })} rows={3} className="w-full px-4 py-2 rounded-xl border border-slate-200 text-sm focus:ring-2 ring-blue-500 outline-none" placeholder="Describe what you offer..." />
                    </div>
                    <div>
                        <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest block mb-1">Target Audience</label>
                        <input type="text" value={formData.target_audience} onChange={e => setFormData({ ...formData, target_audience: e.target.value })} className="w-full px-4 py-2 rounded-xl border border-slate-200 text-sm focus:ring-2 ring-blue-500 outline-none" placeholder="e.g. HNWIs, Collectors" />
                    </div>
                    <div>
                        <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest block mb-1">Primary CTA</label>
                        <input type="text" value={formData.primary_cta} onChange={e => setFormData({ ...formData, primary_cta: e.target.value })} className="w-full px-4 py-2 rounded-xl border border-slate-200 text-sm focus:ring-2 ring-blue-500 outline-none" placeholder="e.g. Book a Consultation, Buy Now" />
                    </div>
                </div>
                <div className="space-y-4">
                    <div>
                        <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest block mb-1">Business Goals (comma separated)</label>
                        <input type="text" value={formData.business_goals} onChange={e => setFormData({ ...formData, business_goals: e.target.value })} className="w-full px-4 py-2 rounded-xl border border-slate-200 text-sm focus:ring-2 ring-blue-500 outline-none" placeholder="e.g. Lead Gen, Brand Awareness" />
                    </div>
                    <div>
                        <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest block mb-1">Color Preferences (comma separated)</label>
                        <input type="text" value={formData.color_preferences} onChange={e => setFormData({ ...formData, color_preferences: e.target.value })} className="w-full px-4 py-2 rounded-xl border border-slate-200 text-sm focus:ring-2 ring-blue-500 outline-none" placeholder="e.g. Midnight Blue, Gold, White" />
                    </div>
                    <div>
                        <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest block mb-1">Content Focus (comma separated)</label>
                        <input type="text" value={formData.content_focus} onChange={e => setFormData({ ...formData, content_focus: e.target.value })} className="w-full px-4 py-2 rounded-xl border border-slate-200 text-sm focus:ring-2 ring-blue-500 outline-none" placeholder="e.g. Heritage, Craftsmanship, Trust" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest block mb-1">Style</label>
                            <select value={formData.style} onChange={e => setFormData({ ...formData, style: e.target.value })} className="w-full px-4 py-2 rounded-xl border border-slate-200 text-sm focus:ring-2 ring-blue-500 outline-none bg-white">
                                <option value="modern">Modern</option>
                                <option value="classic">Classic</option>
                                <option value="minimalist">Minimalist</option>
                                <option value="bold">Bold</option>
                            </select>
                        </div>
                        <div>
                            <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest block mb-1">Layout</label>
                            <select value={formData.layout} onChange={e => setFormData({ ...formData, layout: e.target.value })} className="w-full px-4 py-2 rounded-xl border border-slate-200 text-sm focus:ring-2 ring-blue-500 outline-none bg-white">
                                <option value="standard">Standard</option>
                                <option value="one-page">One-Pager</option>
                                <option value="multi-page">Multi-Page</option>
                                <option value="portfolio">Portfolio</option>
                            </select>
                        </div>
                    </div>
                    <div>
                        <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest block mb-1">Brand Voice</label>
                        <input type="text" value={formData.brand_voice} onChange={e => setFormData({ ...formData, brand_voice: e.target.value })} className="w-full px-4 py-2 rounded-xl border border-slate-200 text-sm focus:ring-2 ring-blue-500 outline-none" placeholder="e.g. Sophisticated, Authoritative, Friendly" />
                    </div>
                </div>
            </div>
            <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest block mb-1">Inspiration Keywords (comma separated)</label>
                        <input type="text" value={formData.inspiration_keywords} onChange={e => setFormData({ ...formData, inspiration_keywords: e.target.value })} className="w-full px-4 py-2 rounded-xl border border-slate-200 text-sm focus:ring-2 ring-blue-500 outline-none" placeholder="e.g. Rolex, Apple, Patek Philippe" />
                    </div>
                    <div>
                        <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest block mb-1">Top Competitors (comma separated)</label>
                        <input type="text" value={formData.competitors} onChange={e => setFormData({ ...formData, competitors: e.target.value })} className="w-full px-4 py-2 rounded-xl border border-slate-200 text-sm focus:ring-2 ring-blue-500 outline-none" placeholder="e.g. competitor1.com, competitor2.com" />
                    </div>
                </div>
                <div>
                    <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest block mb-1">Additional Requirements</label>
                    <textarea value={formData.additional_requirements} onChange={e => setFormData({ ...formData, additional_requirements: e.target.value })} rows={3} className="w-full px-4 py-2 rounded-xl border border-slate-200 text-sm focus:ring-2 ring-blue-500 outline-none" placeholder="Any specific features or constraints..." />
                </div>
            </div>
            <div className="flex justify-center">
                <button
                    onClick={handleSubmit}
                    disabled={loading}
                    className="px-8 py-3 bg-slate-900 text-white rounded-2xl text-sm font-black uppercase tracking-widest hover:bg-slate-800 transition-all disabled:opacity-50 flex items-center gap-2"
                >
                    {loading ? <Loader2 size={18} className="animate-spin" /> : 'Initialize Project'} <ArrowRight size={18} />
                </button>
            </div>
            {error && (
                <div className="p-4 bg-red-50 border border-red-100 text-red-600 text-xs font-bold text-center rounded-2xl">
                    {error}
                </div>
            )}
        </div>
    );
};

const InspirationGallery = ({ projectId, questionnaire, onComplete }: { projectId: string; questionnaire: any; onComplete: (selection: any) => void }) => {
    const [inspirations, setInspirations] = useState<any[]>([]);
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchInspirations = async () => {
            setLoading(true);
            setError(null);
            try {
                const keywords = questionnaire.inspiration_keywords || ['modern website', 'luxury', 'minimalist'];
                const results = await moduleService.executeModule('website_builder', {
                    action: 'searchDribbbleInspirations',
                    keywords,
                });
                setInspirations(results);
            } catch (err: any) {
                setError(err.message || 'Failed to fetch inspirations');
            } finally {
                setLoading(false);
            }
        };
        fetchInspirations();
    }, [questionnaire]);

    const toggleSelection = (id: string) => {
        setSelectedIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
    };

    const handleComplete = async () => {
        if (selectedIds.length === 0) {
            setError('Please select at least one inspiration');
            return;
        }
        onComplete({
            projectId,
            selectedInspirationIds: selectedIds,
            colorScheme: 'auto', // In a full version, we'd have a color picker
            typography: 'auto',
        });
    };

    return (
        <div className="max-w-6xl mx-auto p-8 space-y-8">
            <div className="text-center space-y-2">
                <h2 className="text-3xl font-black text-slate-800 uppercase tracking-tight">Design Inspiration</h2>
                <p className="text-slate-400 font-medium">Select the "vibes" and layouts that inspire you</p>
            </div>

            {loading ? (
                <div className="flex flex-col items-center justify-center py-20 space-y-4">
                    <Loader2 size={48} className="animate-spin text-blue-500" />
                    <p className="text-slate-500 font-bold uppercase tracking-widest text-xs">Sourcing from Dribbble...</p>
                </div>
            ) : error ? (
                <div className="p-8 bg-red-50 border border-red-100 text-red-600 text-center rounded-3xl font-bold">
                    {error}
                </div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                    {inspirations.map(insp => (
                        <div
                            key={insp.id}
                            onClick={() => toggleSelection(insp.id)}
                            className={`cursor-pointer group relative aspect-square rounded-3xl overflow-hidden border-4 transition-all ${selectedIds.includes(insp.id) ? 'border-blue-500 ring-4 ring-blue-100 scale-95' : 'border-transparent hover:border-slate-200'}`}
                        >
                            <img src={insp.imageUrl} alt={insp.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                            <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-transparent to-transparent p-4 flex flex-col justify-end">
                                <p className="text-white font-bold text-xs truncate">{insp.title}</p>
                                <p className="text-slate-300 text-[10px] font-medium">{insp.author}</p>
                            </div>
                            {selectedIds.includes(insp.id) && (
                                <div className="absolute top-3 right-3 p-1 bg-blue-500 text-white rounded-full shadow-lg">
                                    <CheckCircle size={16} />
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            <div className="flex justify-center pt-8">
                <button
                    onClick={handleComplete}
                    disabled={loading || selectedIds.length === 0}
                    className="px-12 py-4 bg-slate-900 text-white rounded-2xl text-sm font-black uppercase tracking-widest hover:bg-slate-800 transition-all disabled:opacity-50 flex items-center gap-2"
                >
                    Proceed to Generation <ArrowRight size={18} />
                </button>
            </div>
        </div>
    );
};


const AssetsView = ({ projectId }: { projectId: string }) => {
    const [assets, setAssets] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadingFile, setUploadingFile] = useState({ name: '', content: '' });
    const [error, setError] = useState<string | null>(null);

    const fetchAssets = async () => {
        setLoading(true);
        setError(null);
        try {
            const result = await moduleService.executeModule('website_builder', { action: 'listAssets', projectId });
            setAssets(result.assets);
        } catch (err: any) {
            setError(err.message || 'Failed to fetch assets');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (projectId) {fetchAssets();}
    }, [projectId]);

    const handleUpload = async () => {
        if (!uploadingFile.name || !uploadingFile.content) {return;}
        setUploading(true);
        setError(null);
        try {
            await moduleService.executeModule('website_builder', {
                action: 'uploadAsset',
                projectId,
                path: uploadingFile.name,
                content: uploadingFile.content
            });
            setUploadingFile({ name: '', content: '' });
            await fetchAssets();
        } catch (err: any) {
            setError(err.message || 'Upload failed');
        } finally {
            setUploading(false);
        }
    };

    const handleDelete = async (path: string) => {
        if (!confirm(`Are you sure you want to delete ${path}?`)) {return;}
        setLoading(true);
        try {
            await moduleService.executeModule('website_builder', { action: 'deleteAsset', projectId, path });
            await fetchAssets();
        } catch (err: any) {
            setError(err.message || 'Delete failed');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-end">
                <div>
                    <h2 className="text-xl font-black text-slate-800 uppercase tracking-tight">Asset Library</h2>
                    <p className="text-xs text-slate-400 font-medium">Manage images, icons, and documents for your site</p>
                </div>
                <button onClick={fetchAssets} className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
                    <Loader2 size={16} className={loading ? 'animate-spin' : ''} />
                </button>
            </div>

            <div className="bg-white border border-slate-200 rounded-3xl p-6 shadow-sm space-y-4">
                <div className="flex gap-3">
                    <input
                        type="text"
                        placeholder="File name (e.g. logo.png)"
                        value={uploadingFile.name}
                        onChange={e => setUploadingFile({ ...uploadingFile, name: e.target.value })}
                        className="flex-1 px-4 py-2 rounded-xl border border-slate-200 text-sm outline-none focus:ring-2 ring-blue-500"
                    />
                    <input
                        type="text"
                        placeholder="Content (Base64 or Text)"
                        value={uploadingFile.content}
                        onChange={e => setUploadingFile({ ...uploadingFile, content: e.target.value })}
                        className="flex-1 px-4 py-2 rounded-xl border border-slate-200 text-sm outline-none focus:ring-2 ring-blue-500"
                    />
                    <button
                        onClick={handleUpload}
                        disabled={uploading || !uploadingFile.name}
                        className="px-4 py-2 bg-blue-600 text-white rounded-xl text-xs font-bold uppercase tracking-widest hover:bg-blue-700 disabled:opacity-50"
                    >
                        {uploading ? <Loader2 size={14} className="animate-spin" /> : 'Upload'}
                    </button>
                </div>
                {error && <div className="text-red-500 text-[10px] font-bold">{error}</div>}
            </div>

            <div className="bg-white border border-slate-200 rounded-3xl overflow-hidden shadow-sm">
                <table className="w-full text-left border-collapse">
                    <thead className="bg-slate-50 border-b border-slate-100">
                        <tr>
                            <th className="px-6 py-3 text-[10px] font-black text-slate-400 uppercase tracking-widest">Asset</th>
                            <th className="px-6 py-3 text-[10px] font-black text-slate-400 uppercase tracking-widest">Size</th>
                            <th className="px-6 py-3 text-[10px] font-black text-slate-400 uppercase tracking-widest">Updated</th>
                            <th className="px-6 py-3 text-[10px] font-black text-slate-400 uppercase tracking-widest text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {loading && assets.length === 0 ? (
                            <tr><td colSpan={4} className="px-6 py-12 text-center text-slate-400 text-xs">Loading assets...</td></tr>
                        ) : assets.length === 0 ? (
                            <tr><td colSpan={4} className="px-6 py-12 text-center text-slate-400 text-xs">No assets found in the library.</td></tr>
                        ) : (
                            assets.map((asset, i) => (
                                <tr key={i} className="hover:bg-slate-50 transition-colors">
                                    <td className="px-6 py-4">
                                        <div className="flex items-center gap-2">
                                            <ImageIcon size={14} className="text-slate-400" />
                                            <span className="text-sm font-bold text-slate-700">{asset.path}</span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 text-xs text-slate-500">
                                        {(asset.size / 1024).toFixed(1)} KB
                                    </td>
                                    <td className="px-6 py-4 text-xs text-slate-400">
                                        {new Date(asset.updatedAt).toLocaleDateString()}
                                    </td>
                                    <td className="px-6 py-4 text-right flex justify-end gap-3">
                                        <a href={asset.url} target="_blank" rel="noreferrer" className="text-[10px] font-bold text-blue-600 hover:underline uppercase tracking-widest">View</a>
                                        <button onClick={() => handleDelete(asset.path)} className="text-[10px] font-bold text-rose-600 hover:underline uppercase tracking-widest">Delete</button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

const CMSView = ({ projectId }: { projectId: string }) => {
    const [content, setContent] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [editingItem, setEditingItem] = useState<any | null>(null);
    const [formData, setFormData] = useState({ title: '', body: '', status: 'Draft' });
    const [error, setError] = useState<string | null>(null);

    const fetchContent = async () => {
        setLoading(true);
        setError(null);
        try {
            const result = await moduleService.executeModule('website_builder', { action: 'listContent', projectId });
            setContent(result.content);
        } catch (err: any) {
            setError(err.message || 'Failed to fetch content');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (projectId) {fetchContent();}
    }, [projectId]);

    const handleSave = async () => {
        if (!formData.title) {return;}
        try {
            if (editingItem) {
                await moduleService.executeModule('website_builder', {
                    action: 'updateContent',
                    projectId,
                    contentId: editingItem.id,
                    data: formData
                });
            } else {
                await moduleService.executeModule('website_builder', {
                    action: 'addContent',
                    projectId,
                    data: formData
                });
            }
            setFormData({ title: '', body: '', status: 'Draft' });
            setEditingItem(null);
            await fetchContent();
        } catch (err: any) {
            setError(err.message || 'Save failed');
        }
    };

    const handleDelete = async (id: string) => {
        if (!confirm('Are you sure you want to delete this content?')) {return;}
        try {
            await moduleService.executeModule('website_builder', { action: 'deleteContent', projectId, contentId: id });
            await fetchContent();
        } catch (err: any) {
            setError(err.message || 'Delete failed');
        }
    };

    return (
        <div className="space-y-6">
            <div>
                <h2 className="text-xl font-black text-slate-800 uppercase tracking-tight">Content CMS</h2>
                <p className="text-xs text-slate-400 font-medium">Manage your site's dynamic content and blog posts</p>
            </div>

            <div className="bg-white border border-slate-200 rounded-3xl p-6 shadow-sm space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <input
                        type="text"
                        placeholder="Title"
                        value={formData.title}
                        onChange={e => setFormData({ ...formData, title: e.target.value })}
                        className="px-4 py-2 rounded-xl border border-slate-200 text-sm outline-none focus:ring-2 ring-blue-500"
                    />
                    <select
                        value={formData.status}
                        onChange={e => setFormData({ ...formData, status: e.target.value })}
                        className="px-4 py-2 rounded-xl border border-slate-200 text-sm outline-none focus:ring-2 ring-blue-500 bg-white"
                    >
                        <option value="Draft">Draft</option>
                        <option value="Review">Review</option>
                        <option value="Published">Published</option>
                    </select>
                </div>
                <textarea
                    placeholder="Content body..."
                    value={formData.body}
                    onChange={e => setFormData({ ...formData, body: e.target.value })}
                    rows={3}
                    className="w-full px-4 py-2 rounded-xl border border-slate-200 text-sm outline-none focus:ring-2 ring-blue-500"
                />
                <div className="flex gap-3">
                    <button
                        onClick={handleSave}
                        className="px-4 py-2 bg-blue-600 text-white rounded-xl text-xs font-bold uppercase tracking-widest hover:bg-blue-700"
                    >
                        {editingItem ? 'Update Content' : 'Add Content'}
                    </button>
                    {editingItem && (
                        <button
                            onClick={() => { setEditingItem(null); setFormData({ title: '', body: '', status: 'Draft' }); }}
                            className="px-4 py-2 bg-slate-100 text-slate-600 rounded-xl text-xs font-bold uppercase tracking-widest hover:bg-slate-200"
                        >
                            Cancel
                        </button>
                    )}
                </div>
                {error && <div className="text-red-500 text-[10px] font-bold">{error}</div>}
            </div>

            <div className="bg-white border border-slate-200 rounded-3xl overflow-hidden shadow-sm">
                <table className="w-full text-left border-collapse">
                    <thead className="bg-slate-50 border-b border-slate-100">
                        <tr>
                            <th className="px-6 py-3 text-[10px] font-black text-slate-400 uppercase tracking-widest">Title</th>
                            <th className="px-6 py-3 text-[10px] font-black text-slate-400 uppercase tracking-widest">Status</th>
                            <th className="px-6 py-3 text-[10px] font-black text-slate-400 uppercase tracking-widest">Updated</th>
                            <th className="px-6 py-3 text-[10px] font-black text-slate-400 uppercase tracking-widest text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {loading && content.length === 0 ? (
                            <tr><td colSpan={4} className="px-6 py-12 text-center text-slate-400 text-xs">Loading content...</td></tr>
                        ) : content.length === 0 ? (
                            <tr><td colSpan={4} className="px-6 py-12 text-center text-slate-400 text-xs">No content found.</td></tr>
                        ) : (
                            content.map((item, i) => (
                                <tr key={i} className="hover:bg-slate-50 transition-colors">
                                    <td className="px-6 py-4 text-sm font-bold text-slate-700">{item.title}</td>
                                    <td className="px-6 py-4">
                                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider ${
                                            item.status === 'Published' ? 'bg-emerald-100 text-emerald-600' :
                                            item.status === 'Review' ? 'bg-amber-100 text-amber-600' : 'bg-slate-100 text-slate-600'
                                        }`}>
                                            {item.status}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 text-xs text-slate-400 font-medium">
                                        {new Date(item.updatedAt).toLocaleDateString()}
                                    </td>
                                    <td className="px-6 py-4 text-right flex justify-end gap-3">
                                        <button
                                            onClick={() => { setEditingItem(item); setFormData({ title: item.title, body: item.body || '', status: item.status }); }}
                                            className="text-[10px] font-bold text-blue-600 hover:underline uppercase tracking-widest"
                                        >
                                            Edit
                                        </button>
                                        <button onClick={() => handleDelete(item.id)} className="text-[10px] font-bold text-rose-600 hover:underline uppercase tracking-widest">Delete</button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

const StatsView = () => {
    return (
        <div className="space-y-6">
            <div>
                <h2 className="text-xl font-black text-slate-800 uppercase tracking-tight">Site Intelligence</h2>
                <p className="text-xs text-slate-400 font-medium">Real-time traffic and conversion metrics</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {[
                    { label: 'Total Visitors', value: '12.4k', change: '+12%', icon: Layout },
                    { label: 'Conversion Rate', value: '3.2%', change: '+0.4%', icon: BarChart3 },
                    { label: 'Avg. Session', value: '4m 12s', change: '-2%', icon: MessageSquare },
                ].map((stat, i) => (
                    <div key={i} className="p-6 bg-white border border-slate-100 rounded-3xl shadow-sm">
                        <div className="flex items-center justify-between mb-4">
                            <div className="p-2 bg-slate-100 rounded-xl text-slate-600">
                                <stat.icon size={20} />
                            </div>
                            <span className={`text-xs font-black ${stat.change.startsWith('+') ? 'text-emerald-500' : 'text-rose-500'}`}>
                                {stat.change}
                            </span>
                        </div>
                        <p className="text-slate-400 text-[10px] font-bold uppercase tracking-widest">{stat.label}</p>
                        <h3 className="text-2xl font-black text-slate-800">{stat.value}</h3>
                    </div>
                ))}
            </div>
        </div>
    );
};
